import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { T as DS } from '../design-system/tokens';
// Passkey sign-in is back (2026-09-03). It was retired on 2026-07-29 for "0
// registrations, 0 logins" — but no enrollment UI had ever existed, so nobody
// could have registered one. /passkeys is that missing screen; the button below
// only appears once this browser reports a passkey it can actually offer.
// Google Sign-In + 2FA were removed 2026-05-04 because both flows were broken in
// production and blocked logins. To re-enable Google, see git history.
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { T } from '../design-system';
import { isPartnerHost } from '../utils/partnerHost';
import { APP_NAME } from '../config/brand';

const EyeIcon = ({ off }) => (
  off ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  )
);

// variant="partners" is the partner sign-in (/partners/login and the partner
// portal host): same screen, partner wording, and it lands in the portal
// instead of the ERP.
export default function Login({ variant } = {}) {
  const partners = variant === 'partners';
  // Optional alternative look for the partner sign-in; off by default.
  const look = '';
  // Partner sign-in speaks the reseller's language: English or Russian.
  const [lang, setLangState] = useState(() => {
    try { const v = localStorage.getItem('aone_partner_lang'); if (v) return v; } catch { /* storage blocked */ }
    return typeof navigator !== 'undefined' && /^ru/i.test(navigator.language || '') ? 'ru' : 'en';
  });
  const setLang = (v) => { setLangState(v); try { localStorage.setItem('aone_partner_lang', v); } catch { /* ignore */ } };
  const L = (en, ru) => (partners && lang === 'ru' ? ru : en);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [mustResetPassword, setMustResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetMessage, setResetMessage] = useState('');
  // Forgot-password: null (off) → 'email' (enter email) → 'otp' (enter code);
  // a verified OTP flips into the existing mustResetPassword screen above.
  const [forgotStep, setForgotStep] = useState(null);
  // 2FA step: set when the server answers a login with { requires2FA, challengeToken }.
  // The server gates BOTH password and Google login on the second factor, so this
  // screen must exist — without it a 2FA-enrolled user has no way to finish signing in.
  const [twoFAChallenge, setTwoFAChallenge] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [passkeyReady, setPasskeyReady] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyHint, setPasskeyHint] = useState('');
  const [otp, setOtp] = useState('');
  const [forgotInfo, setForgotInfo] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const { login, forceResetPassword, verify2FA, passkeyLogin } = useAuth();
  const navigate = useNavigate();

  // Post-login destination: honour a ?next= deep link (e.g. the BMR "View full
  // BMR" button) but only when it's a safe in-app relative path — never an
  // absolute or protocol-relative URL, to avoid an open-redirect.
  const nextPath = () => {
    try {
      const raw = new URLSearchParams(window.location.search).get('next');
      if (raw) {
        const dec = decodeURIComponent(raw);
        if (dec.startsWith('/') && !dec.startsWith('//')) return dec;
      }
    } catch { /* fall through to default */ }
    return partners ? '/partners/catalogue' : '/';
  };
  // Staff who sign in on the partner screen belong in the ERP, not the portal.
  const afterLogin = (u) => {
    if (partners && u?.role && u.role !== 'partner') {
      const erpUrl = String(process.env.REACT_APP_PUBLIC_APP_URL || '').replace(/\/+$/, '');
      if (isPartnerHost() && erpUrl) { window.location.href = `${erpUrl}/partner-admin`; return; }
      navigate('/partner-admin'); return;
    }
    navigate(nextPath());
  };

  // Only offer the button when the browser has a passkey for this site to
  // show. Offering it unconditionally is what the retired version did, and it
  // could only ever fail — then blame the user's device for it.
  useEffect(() => {
    let alive = true;
    import('@simplewebauthn/browser')
      .then(async ({ browserSupportsWebAuthn, browserSupportsWebAuthnAutofill }) => {
        if (!alive || !browserSupportsWebAuthn()) return;
        const ready = await browserSupportsWebAuthnAutofill().catch(() => false);
        if (alive) setPasskeyReady(ready || browserSupportsWebAuthn());
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Silent discovery: if this device holds a passkey for the site, the
  // browser lists it in the email field's autofill; picking it signs in.
  // No passkey → nothing appears. Never opens a dialog on its own.
  useEffect(() => {
    if (!passkeyReady) return undefined;
    let alive = true;
    (async () => {
      try {
        const { browserSupportsWebAuthnAutofill } = await import('@simplewebauthn/browser');
        if (!(await browserSupportsWebAuthnAutofill())) return;
        const result = await passkeyLogin({ autofill: true });
        if (!alive) return;
        if (result?.requires2FA) { setTwoFAChallenge(result.challengeToken); setTwoFACode(''); }
        else if (result?.mustResetPassword) { setMustResetPassword(true); setResetToken(result.resetToken); setResetMessage(result.message); }
        else navigate(nextPath());
      } catch (e) {
        // Aborted (another request took over, or the page left) or no passkey chosen — both are silence.
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeyReady]);

  const handlePasskeyLogin = async () => {
    setPasskeyBusy(true); setError(''); setPasskeyHint('');
    try {
      const result = await passkeyLogin();
      if (result?.requires2FA) {
        setTwoFAChallenge(result.challengeToken);
        setTwoFACode('');
      } else if (result?.mustResetPassword) {
        setMustResetPassword(true);
        setResetToken(result.resetToken);
        setResetMessage(result.message);
      } else {
        navigate(nextPath());
      }
    } catch (err) {
      // Closing the system sheet is a choice, not a failure to report.
      if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') {
        // Closing the sheet is a choice — but on a device with no passkey the
        // sheet was the phone/QR one, so say what to do instead of nothing.
        setPasskeyHint('No passkey on this device yet. Sign in with your password, then add Touch ID under Settings → Passkeys.');
      }
      else setError(err.response?.data?.error || 'Could not sign in with that passkey. Use your password instead.');
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleEmailLogin = async (e) => {
    if (e) e.preventDefault();
    if (!email || !password) { setError('Email and password required'); return; }
    setLoading(true); setError('');
    try {
      const result = await login(email, password);
      if (result?.requires2FA) {
        setTwoFAChallenge(result.challengeToken);
        setTwoFACode('');
      } else if (result?.mustResetPassword) {
        setMustResetPassword(true);
        setResetToken(result.resetToken);
        setResetMessage(result.message);
      } else {
        afterLogin(result);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    if (e) e.preventDefault();
    if (!twoFACode) { setError('Enter the 6-digit code from your authenticator app'); return; }
    setLoading(true); setError('');
    try {
      const result = await verify2FA(null, twoFACode, twoFAChallenge);
      // A 2FA user can also be flagged for a password reset — the server applies
      // both policies, so honour the reset hand-off here too rather than dropping
      // the user into the app with no session.
      if (result?.mustResetPassword) {
        setTwoFAChallenge(null);
        setMustResetPassword(true);
        setResetToken(result.resetToken);
        setResetMessage(result.message);
        return;
      }
      navigate(nextPath());
    } catch (err) {
      // The challenge token lives 5 minutes; past that the user must start over.
      if (err.response?.status === 401 && !err.response?.data?.error?.includes('2FA code')) {
        setTwoFAChallenge(null);
        setError('Your sign-in request expired. Please enter your password again.');
      } else {
        setError(err.response?.data?.error || 'Invalid code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForceResetPassword = async (e) => {
    if (e) e.preventDefault();
    if (!newPassword || !confirmPassword) { setError('Both password fields are required'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(newPassword)) { setError('Password must contain an uppercase letter'); return; }
    if (!/[a-z]/.test(newPassword)) { setError('Password must contain a lowercase letter'); return; }
    if (!/[0-9]/.test(newPassword)) { setError('Password must contain a number'); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)) { setError('Password must contain a special character'); return; }
    setLoading(true); setError('');
    try {
      await forceResetPassword(resetToken, newPassword);
      navigate(nextPath());
    } catch (err) {
      const msg = err.response?.data?.error || 'Password reset failed. Please try again.';
      // Reset token expired (15 min TTL) — take the user back to the login form
      if (err.response?.status === 401) {
        setMustResetPassword(false);
        setResetToken(null);
        setError('Your session expired. Please sign in again to set your new password.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    if (e) e.preventDefault();
    if (!email) { setError('Enter your email first'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setForgotInfo(data.message || 'If this email exists, a one-time code has been sent.');
      setForgotStep('otp');
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the code. Try again.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) { setError('Enter the 6-digit code from your email'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/verify-reset-otp', { email, otp: otp.trim() });
      // Hand off to the existing set-new-password screen.
      setResetToken(data.resetToken);
      setResetMessage(data.message || 'Code verified — set your new password.');
      setMustResetPassword(true);
      setForgotStep(null);
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired code');
    } finally { setLoading(false); }
  };

  const isTagline = !mustResetPassword && !forgotStep && !twoFAChallenge;
  const headline = mustResetPassword ? 'Renew your key.'
    : twoFAChallenge ? 'One more key.'
    : forgotStep ? 'Recover your key.'
    : partners ? `${APP_NAME} Partners`
    : APP_NAME;
  const sub = mustResetPassword ? (resetMessage || 'Set a new password to continue.')
    : twoFAChallenge ? 'Enter the 6-digit code from your authenticator app.'
    : forgotStep === 'email' ? 'Tell us your email — we will send a one-time code.'
    : forgotStep === 'otp' ? (forgotInfo || 'Enter the 6-digit code from your email.')
    : '';

  return (
    <>
      <style>{`
        /* Playfair Display is loaded globally in index.html — no per-page @import needed */
        @keyframes aoneLoginSpin { to { transform: rotate(360deg); } }
        @keyframes aoneLoginRise { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
        @keyframes aoneLoginSweep { 0%, 72% { left:-60%; } 100% { left:130%; } }

        html, body { background:#F8F7F3; }
        body {
          font-family:${DS.font};
          -webkit-font-smoothing:antialiased;
        }

        .aone-login-page,
        .aone-login-page * {
          box-sizing:border-box;
        }

        .aone-login-page {
          --paper:#F8F7F3;
          --surface:#FFFEFB;
          --ink:#1C2230;
          --ink-soft:#4A555A;
          --muted:#8A9498;
          --hairline:rgba(184,155,94,.35);
          --hairline-soft:rgba(28,34,48,.10);
          --brass:#B89B5E;
          --gold:#8A6E32;
          --wine:${T.accent};
          --wine-deep:#591722;
          --cream:#F4E8D7;
          --red:#8A3A48;
          --serif:'Playfair Display', Georgia, 'Times New Roman', serif;
          --script:'Pinyon Script', 'Playfair Display', Georgia, cursive;
          position:relative;
          min-height:100vh;
          display:flex;
          align-items:center;
          justify-content:center;
          padding:56px 20px;
          background:
            radial-gradient(90% 70% at 50% 0%, #FFFEFB 0%, var(--paper) 60%),
            var(--paper);
          background-image:
            radial-gradient(rgba(184,155,94,.22) 1px, transparent 1.4px),
            radial-gradient(90% 70% at 50% 0%, #FFFEFB 0%, var(--paper) 60%);
          background-size:26px 26px, auto;
          color:var(--ink);
          overflow:hidden;
        }

        .aone-login-watermark {
          position:absolute;
          right:-112px;
          bottom:-128px;
          width:448px;
          opacity:.05;
          pointer-events:none;
        }

        .aone-login-shell {
          width:100%;
          max-width:320px;
          text-align:center;
          position:relative;
          z-index:1;
          animation:aoneLoginRise .6s ease both;
        }

        .aone-login-crest { margin-bottom:14px; }
        .aone-login-logo {
          width:200px;
          height:auto;
          display:block;
          margin:0 auto;
          opacity:.92;
        }

        .aone-login-rule {
          display:flex;
          align-items:center;
          gap:12px;
          margin:28px 0 32px;
        }
        .aone-login-rule span { flex:1; height:1px; background:var(--hairline); }
        .aone-login-rule i { font-style:normal; color:var(--gold); font-size:10px; }

        .aone-login-headline {
          margin:0 auto 10px;
          font-family:var(--serif);
          font-style:normal;
          font-size:clamp(30px, 6vw, 46px);
          line-height:1.12;
          font-weight:500;
          letter-spacing:.005em;
          color:var(--wine);
          max-width:92vw;
        }
        /* Brand tagline (default state) — echoes the gold, letter-spaced crest banner.
           Sized ~10% down from 27px and kept to a single line via nowrap + fit-content;
           scales down on narrow viewports so it never wraps or overflows. */
        /* Premium tagline — italic serif in wine, the emotional first note. */
        .aone-login-headline--tagline {
          text-transform:none;
          width:auto;
          max-width:100%;
          margin:0 auto 6px;
          font-family:var(--serif);
          font-style:italic;
          font-size:clamp(20px, 5.2vw, 24px);
          line-height:1.3;
          font-weight:400;
          letter-spacing:.01em;
          color:var(--wine);
        }
        .aone-login-welcome {
          margin:0 0 34px;
          font-size:11px;
          letter-spacing:.28em;
          text-transform:uppercase;
          color:var(--muted);
        }
        .aone-login-sub {
          margin:0 0 30px;
          font-size:13px;
          line-height:1.6;
          color:var(--muted);
        }

        .aone-login-card { text-align:center; }

        /* Floating-label underline fields — no boxes; the label lifts into small
           gold caps and a brass→gold rule draws itself on focus. Shared by every
           state (sign-in, forgot, OTP, reset) so the whole flow stays coherent.
           Relies on placeholder=" " so :placeholder-shown tracks the empty state. */
        .aone-login-field { margin-bottom:26px; text-align:left; }
        .aone-login-input-wrap { position:relative; width:100%; margin:0 auto; }
        .aone-login-input {
          width:100%;
          height:40px;
          border:0;
          border-bottom:1px solid var(--hairline-soft);
          border-radius:0;
          background:transparent;
          color:var(--ink);
          padding:0 30px 0 0;
          font:inherit;
          font-size:14px;
          text-align:left;
          outline:none;
          transition:border-color .25s ease;
        }
        .aone-login-input::placeholder { color:transparent; }
        .aone-login-field label {
          position:absolute;
          left:0;
          top:11px;
          margin:0;
          font-size:14px;
          font-weight:400;
          letter-spacing:.01em;
          text-transform:none;
          color:var(--muted);
          pointer-events:none;
          transition:.22s cubic-bezier(.2,.7,.2,1);
        }
        .aone-login-input:focus ~ label,
        .aone-login-input:not(:placeholder-shown) ~ label {
          top:-11px;
          font-size:10px;
          font-weight:600;
          letter-spacing:.2em;
          text-transform:uppercase;
          color:var(--gold);
        }
        .aone-login-underline {
          position:absolute;
          left:0;
          bottom:0;
          height:1px;
          width:0;
          background:linear-gradient(90deg, var(--brass), var(--gold));
          transition:width .35s cubic-bezier(.2,.7,.2,1);
          pointer-events:none;
        }
        .aone-login-input:focus ~ .aone-login-underline { width:100%; }
        .aone-login-input-icon {
          position:absolute;
          right:2px;
          top:11px;
          color:var(--brass);
          font-size:14px;
          pointer-events:none;
        }
        .aone-login-eye {
          position:absolute;
          right:0;
          top:8px;
          background:transparent;
          border:none;
          color:var(--muted);
          cursor:pointer;
          padding:4px;
          display:flex;
          align-items:center;
          justify-content:center;
        }
        .aone-login-eye:hover { color:var(--wine); }

        .aone-login-row {
          display:flex;
          align-items:center;
          justify-content:space-between;
          margin:-6px 0 30px;
          gap:16px;
        }
        .aone-login-check {
          display:inline-flex;
          align-items:center;
          gap:9px;
          color:var(--ink-soft);
          font-size:12px;
          cursor:pointer;
        }
        .aone-login-check input { width:15px; height:15px; accent-color:var(--wine); }
        .aone-login-link {
          color:var(--gold);
          text-decoration:none;
          font-size:10px;
          font-weight:600;
          letter-spacing:.14em;
          text-transform:uppercase;
        }
        .aone-login-link:hover { color:var(--wine); }

        .aone-login-primary,
        .aone-login-secondary {
          width:100%;
          margin-left:auto;
          margin-right:auto;
          border-radius:10px;
          font:inherit;
          font-weight:600;
          text-transform:uppercase;
          cursor:pointer;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:11px;
          transition:transform .2s ease, box-shadow .25s ease, background .18s ease, color .18s ease, border-color .18s ease;
        }
        /* The "foiled key" — deep wine gradient, inset gold hairline, inner sheen
           and a slow light-sweep. This is THE object you press to get in. */
        .aone-login-primary {
          position:relative;
          overflow:hidden;
          height:54px;
          border:0;
          font-size:11px;
          letter-spacing:.3em;
          color:var(--cream);
          background:linear-gradient(180deg, #7D2432 0%, var(--wine) 55%, var(--wine-deep) 100%);
          box-shadow:0 12px 28px rgba(110,31,43,.28), inset 0 1px 0 rgba(255,255,255,.14), inset 0 0 0 1px rgba(184,155,94,.35);
        }
        .aone-login-primary > * { position:relative; z-index:2; }
        .aone-login-sheen {
          position:absolute;
          top:0;
          left:-60%;
          width:40%;
          height:100%;
          z-index:1;
          background:linear-gradient(100deg, transparent, rgba(244,232,215,.35), transparent);
          transform:skewX(-18deg);
          animation:aoneLoginSweep 4.5s ease-in-out infinite;
          pointer-events:none;
        }
        .aone-login-primary:hover:not(:disabled) {
          transform:translateY(-1px);
          box-shadow:0 16px 34px rgba(110,31,43,.34), inset 0 1px 0 rgba(255,255,255,.18), inset 0 0 0 1px rgba(184,155,94,.55);
        }
        .aone-login-secondary {
          height:48px;
          border:1px solid var(--hairline);
          background:transparent;
          color:var(--ink-soft);
          font-size:10.5px;
          letter-spacing:.24em;
        }
        .aone-login-secondary:hover:not(:disabled) { border-color:var(--brass); color:var(--ink); background:rgba(244,232,215,.5); }
        .aone-login-primary:disabled,
        .aone-login-secondary:disabled { opacity:.55; cursor:not-allowed; }
        .aone-login-or {
          display:flex;
          align-items:center;
          gap:14px;
          margin:18px 0;
        }
        .aone-login-or span { flex:1; height:1px; background:var(--hairline-soft); }
        .aone-login-or i {
          font-style:normal;
          font-size:9px;
          letter-spacing:.24em;
          text-transform:uppercase;
          color:var(--muted);
        }
        .aone-login-passkey {
          width:100%;
          display:flex;
          align-items:center;
          justify-content:center;
          gap:9px;
          padding:13px 18px;
          background:transparent;
          border:1px solid var(--hairline);
          border-radius:2px;
          color:var(--ink);
          font-family:inherit;
          font-size:11px;
          letter-spacing:.24em;
          text-transform:uppercase;
          cursor:pointer;
          transition:border-color .18s ease, background .18s ease;
        }
        .aone-login-passkey:hover:not(:disabled) { border-color:var(--brass); background:rgba(244,232,215,.5); }
        .aone-login-passkey:disabled { opacity:.55; cursor:not-allowed; }
        .aone-login-passkey svg { flex:none; }

        .aone-login-loading {
          display:flex;
          align-items:center;
          justify-content:center;
          gap:10px;
        }
        .aone-login-loading svg { animation:aoneLoginSpin 1s linear infinite; }

        .aone-login-security {
          margin-top:34px;
          display:flex;
          align-items:center;
          justify-content:center;
          gap:18px;
        }
        .aone-login-security div {
          color:var(--muted);
          font-size:8.5px;
          font-weight:600;
          text-transform:uppercase;
          letter-spacing:.28em;
          position:relative;
        }
        .aone-login-security div:not(:last-child)::after {
          content:"";
          position:absolute;
          right:-9px;
          top:50%;
          width:3px;
          height:3px;
          border-radius:50%;
          background:var(--brass);
          transform:translateY(-50%);
        }

        .aone-login-error {
          display:flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          background:rgba(138,58,72,.06);
          border:1px solid rgba(138,58,72,.28);
          border-radius:8px;
          padding:11px 13px;
          font-size:12.5px;
          color:var(--red);
          font-weight:500;
          margin-bottom:20px;
        }
        .aone-login-policy {
          background:rgba(184,155,94,.10);
          border:1px solid rgba(184,155,94,.32);
          border-radius:8px;
          padding:12px 14px;
          margin-bottom:22px;
          color:#6B551F;
          font-size:11.5px;
          line-height:1.55;
          font-weight:500;
        }

        @media (max-width: 480px) {
          .aone-login-page { padding:40px 20px; }
          .aone-login-logo { width:156px; }
          .aone-login-row {
            align-items:center;
            flex-direction:column;
            gap:12px;
          }
          /* Robust centering + full-width fields on phones:
             the desktop left:50%/translateX trick mis-centers a fit-content
             block here, and 60%-wide fields clip their placeholder + icons. */
          .aone-login-headline--tagline {
            position:static;
            left:auto;
            transform:none;
            width:auto;
            max-width:100%;
            white-space:normal;
          }
          .aone-login-input-wrap,
          .aone-login-primary,
          .aone-login-secondary {
            width:100%;
          }
        }

        /* ── Partner sign-in: split screen, modern boxed fields ── */
        .aone-login-page.pp-login { display:grid; grid-template-columns:minmax(0,1.15fr) minmax(380px,1fr); min-height:100vh; padding:0; background:#FBF8F5; align-items:stretch; justify-items:stretch; }
        .pp-login-hero { position:relative; overflow:hidden; background:#22080E; color:#fff; display:flex; flex-direction:column; justify-content:space-between; padding:40px 48px; }
        .pp-login-hero-shade { position:absolute; inset:0; background:linear-gradient(180deg, rgba(34,8,14,.35) 0%, rgba(34,8,14,0) 14%, rgba(34,8,14,0) 100%); }
        .pp-login-hero-top, .pp-login-hero-copy { position:relative; z-index:1; }
        .pp-login-mark { font-family:var(--serif); font-size:22px; font-weight:600; letter-spacing:-.01em; text-shadow:0 1px 12px rgba(0,0,0,.35); }
        .pp-login-mark em { font-style:normal; color:#F2C9CF; }
        .pp-login-mark--panel { color:var(--ink); margin-bottom:34px; display:none; text-shadow:none; }
        .pp-login-mark--panel em { color:var(--wine); }
        .pp-login-eyebrow { font-size:11px; letter-spacing:.24em; text-transform:uppercase; color:#F2C9CF; margin:0 0 12px; font-weight:600; }
        .pp-login-hero-copy h2 { font-family:var(--serif); font-weight:500; font-size:clamp(26px, 2.6vw, 38px); line-height:1.18; margin:0 0 22px; max-width:560px; letter-spacing:-.01em; }
        .pp-login-hero-copy ul { list-style:none; padding:0; margin:0 0 28px; display:grid; gap:10px; max-width:520px; }
        .pp-login-hero-copy li { position:relative; padding-left:26px; font-size:15px; line-height:1.45; color:rgba(255,255,255,.92); }
        .pp-login-hero-copy li::before { content:''; position:absolute; left:0; top:7px; width:14px; height:8px; border-left:2px solid #F2C9CF; border-bottom:2px solid #F2C9CF; transform:rotate(-45deg); }
        .pp-login-brands { display:flex; flex-wrap:wrap; gap:8px 22px; padding-top:20px; border-top:1px solid rgba(255,255,255,.22); font-size:12px; letter-spacing:.16em; text-transform:uppercase; color:rgba(255,255,255,.8); }
        .aone-login-shell.pp-login-panel { position:relative; width:100%; max-width:none; margin:0; padding:56px clamp(24px, 6vw, 88px); display:flex; flex-direction:column; justify-content:center; background:#FFFFFF; box-shadow:none; border:0; text-align:left; animation:none; }
        .pp-login-panel > * { width:100%; max-width:400px; margin-left:auto; margin-right:auto; }
        .pp-login-lang { position:absolute; top:24px; right:28px; width:auto!important; margin:0!important; display:flex; gap:2px; background:#F3EEEA; border-radius:999px; padding:3px; }
        .pp-login-lang button { border:0; background:transparent; padding:6px 12px; border-radius:999px; font:inherit; font-size:12px; font-weight:600; letter-spacing:.06em; color:var(--ink-soft); cursor:pointer; }
        .pp-login-lang button[aria-pressed="true"] { background:#fff; color:var(--wine); box-shadow:0 1px 3px rgba(0,0,0,.12); }
        .pp-login-title { font-family:var(--serif); font-size:30px; line-height:1.15; font-weight:600; color:var(--ink); margin:0 auto 10px; letter-spacing:-.015em; text-align:left; }
        .pp-login-lead { color:var(--ink-soft); font-size:15px; line-height:1.5; margin:0 auto 30px; text-align:left; }
        .pp-login .aone-login-card { text-align:left; background:transparent; border:0; box-shadow:none; padding:0; width:100%!important; max-width:400px!important; margin-left:auto!important; margin-right:auto!important; }
        .pp-login .aone-login-card form, .pp-login .aone-login-input-wrap { width:100%; max-width:none; }
        .pp-login .aone-login-field { margin-bottom:16px; }
        .pp-login .aone-login-input { height:52px; border:1px solid #E4DAD2; border-radius:12px; background:#fff; padding:18px 44px 0 14px; font-size:15px; transition:border-color .2s, box-shadow .2s; }
        .pp-login .aone-login-input:focus { border-color:var(--wine); box-shadow:0 0 0 4px rgba(110,31,43,.10); }
        .pp-login .aone-login-field label { left:15px; top:16px; font-size:14px; color:#8A8078; }
        .pp-login .aone-login-input:focus ~ label, .pp-login .aone-login-input:not(:placeholder-shown) ~ label { top:7px; font-size:11px; letter-spacing:.04em; text-transform:none; font-weight:600; color:var(--wine); }
        .pp-login .aone-login-underline { display:none; }
        .pp-login .aone-login-input-icon { right:16px; top:17px; color:#B7ABA2; }
        .pp-login .aone-login-eye { right:10px; top:14px; }
        .pp-login .aone-login-row { margin:6px 0 22px; }
        .pp-login .aone-login-link { letter-spacing:.02em; text-transform:none; font-size:13px; font-weight:600; }
        .pp-login .aone-login-primary { height:52px; border-radius:12px; font-size:15px; letter-spacing:.02em; text-transform:none; font-weight:600; background:var(--wine); box-shadow:0 10px 24px rgba(110,31,43,.22); }
        .pp-login .aone-login-primary:hover { background:var(--wine-deep); }
        .pp-login .aone-login-sheen, .pp-login .aone-login-primary > span:not(.aone-login-loading) svg { display:none; }
        .pp-login-foot { margin:28px auto 0; font-size:12px; color:#9A8F87; text-align:left; }
        /* ── Look B · full-bleed banner, floating card ── */
        .aone-login-page.pp-login.pp-look-b { display:flex; align-items:center; justify-content:flex-start!important; position:relative; background:#2A1410; }
        .pp-look-b::before { content:''; position:absolute; inset:0; background:linear-gradient(90deg, rgba(26,10,8,.62) 0%, rgba(26,10,8,.35) 34%, rgba(26,10,8,0) 58%); pointer-events:none; }
        .pp-look-b .pp-login-hero { position:absolute; inset:0; background:none; padding:28px clamp(20px, 4vw, 56px); pointer-events:none; z-index:1; }
        .pp-look-b .pp-login-hero-shade { background:linear-gradient(0deg, rgba(20,8,6,.55) 0%, rgba(20,8,6,0) 22%); }
        .pp-look-b .pp-login-hero-copy { align-self:flex-end; text-align:right; }
        .pp-look-b .pp-login-hero-copy h2, .pp-look-b .pp-login-hero-copy ul { display:none; }
        .pp-look-b .pp-login-eyebrow { color:rgba(255,255,255,.85); margin-bottom:10px; }
        .pp-look-b .pp-login-brands { border-top:0; padding-top:0; justify-content:flex-end; }
        .pp-look-b .aone-login-shell.pp-login-panel { position:relative; z-index:2; flex:0 0 auto; width:min(460px, calc(100vw - 32px)); margin:48px auto 48px clamp(16px, 7vw, 120px)!important; padding:44px 40px 34px; background:rgba(255,255,255,.97); border-radius:24px; box-shadow:0 30px 80px rgba(20,6,4,.35); backdrop-filter:blur(8px); min-height:0; }
        .pp-look-b .pp-login-panel > * { max-width:none; }
        .pp-look-b .pp-login-mark--panel { display:block; margin-bottom:26px; font-size:20px; }
        .pp-look-b .pp-login-lang { top:22px; right:22px; }

        @media (max-width: 900px) {
          .aone-login-page.pp-login.pp-look-b { display:block; background:#2A1410; padding-top:62vw; }
          .pp-look-b::before { background:linear-gradient(180deg, rgba(26,10,8,0) 30%, #2A1410 62vw); }
          .pp-look-b .pp-login-hero { display:none; }
          .pp-look-b .aone-login-shell.pp-login-panel { margin:0 auto 32px; width:calc(100vw - 24px); padding:30px 22px 26px; }
        }
        @media (max-width: 900px) {
          .aone-login-page.pp-login { grid-template-columns:1fr; }
          .pp-login-hero { min-height:260px; padding:24px; justify-content:flex-end; }
          .pp-login-hero-top { display:none; }
          .pp-login-hero-copy h2 { font-size:22px; margin-bottom:0; }
          .pp-login-hero-copy ul, .pp-login-brands, .pp-login-eyebrow { display:none; }
          .aone-login-shell.pp-login-panel { padding:36px 20px 48px; }
          .pp-login-mark--panel { display:block; margin-bottom:22px; }
          .pp-login-lang { top:16px; right:16px; }
          .pp-login .aone-login-row { flex-direction:row!important; justify-content:space-between!important; align-items:center!important; gap:8px!important; margin:4px 0 20px!important; }
          .pp-login .aone-login-primary { margin-top:0!important; }
        }
      `}</style>

      <main className={partners ? `aone-login-page pp-login${look ? ` pp-look-${look}` : ''}` : 'aone-login-page'}>
        {partners ? (
          <aside className="pp-login-hero" aria-hidden="true">
            <div className="pp-login-hero-shade" />
            <div className="pp-login-hero-top">
              <span className="pp-login-mark">{APP_NAME} <em>Partners</em></span>
            </div>
            <div className="pp-login-hero-copy">
              <p className="pp-login-eyebrow">{L('Trade portal', 'Торговый портал')}</p>
              <h2>{L('Your trade account, prices and product specifications in one place.', 'Ваш торговый аккаунт, цены и спецификации товаров — в одном месте.')}</h2>
              <ul>
                <li>{L('Your trade prices in USD, shown in your currency', 'Ваши оптовые цены в USD — с пересчётом в вашу валюту')}</li>
                <li>{L('Specifications, HS codes, cartons and warnings for every product', 'Спецификации, коды ТН ВЭД, упаковка и предупреждения по каждому товару')}</li>
                <li>{L('One account for your whole team', 'Один аккаунт для всей вашей команды')}</li>
              </ul>
            </div>
          </aside>
        ) : null}

        <section className={partners ? 'aone-login-shell pp-login-panel' : 'aone-login-shell'} aria-label={partners ? `Sign in to ${APP_NAME} Partners` : `Sign in to ${APP_NAME}`}>
          {partners ? (
            <>
              <div className="pp-login-lang" role="group" aria-label="Language">
                <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')}>EN</button>
                <button type="button" aria-pressed={lang === 'ru'} onClick={() => setLang('ru')}>RU</button>
              </div>
              <div className="pp-login-mark pp-login-mark--panel">{APP_NAME} <em>Partners</em></div>
              <h1 className="pp-login-title">{isTagline ? L('Sign in to your trade account', 'Вход в торговый аккаунт') : headline}</h1>
              {isTagline
                ? <p className="pp-login-lead">{L('For approved distributors, importers and retailers.', 'Для одобренных дистрибьюторов, импортёров и розничных партнёров.')}</p>
                : sub && <p className="pp-login-lead">{sub}</p>}
            </>
          ) : (
            <>
              <div className="aone-login-crest">
                <img src="/logo.png" alt={APP_NAME} style={{ width: 96, height: 96, display: 'block', margin: '0 auto 10px' }} />
                <div className="aone-login-wordmark" style={{ fontFamily: 'var(--serif)', fontSize: 28, fontWeight: 600, letterSpacing: '0.04em' }}>{APP_NAME}</div>
              </div>

              <div className="aone-login-rule" aria-hidden="true"><span /><i>&#10022;</i><span /></div>

              {/* The wordmark above already names the app; the headline only speaks on reset/2FA/recovery. */}
              {!isTagline && <h1 className="aone-login-headline">{headline}</h1>}
              {isTagline && <p className="aone-login-welcome">Sign in to continue</p>}
              {sub && <p className="aone-login-sub">{sub}</p>}
            </>
          )}

          <div className="aone-login-card">
            {error && (
              <div className="aone-login-error">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                <span>{error}</span>
              </div>
            )}

            {twoFAChallenge ? (
              <form onSubmit={handleVerify2FA}>
                <div className="aone-login-field">
                  <div className="aone-login-input-wrap">
                    <input
                      id="twofa"
                      className="aone-login-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder=" "
                      value={twoFACode}
                      onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                      style={{ letterSpacing: 8, textAlign: 'center', fontSize: DS.type.xl, paddingRight: 0 }}
                      autoComplete="one-time-code"
                      data-no-uppercase="true"
                      required
                      autoFocus
                    />
                    <label htmlFor="twofa">6-digit code</label>
                    <span className="aone-login-underline" />
                  </div>
                </div>
                <button type="submit" className="aone-login-primary" disabled={loading}>
                  {loading ? 'Verifying…' : 'Verify code'}
                </button>
                <div className="aone-login-row" style={{ marginTop: 10 }}>
                  <a className="aone-login-link" href="#back" onClick={(e) => {
                    e.preventDefault();
                    setTwoFAChallenge(null); setTwoFACode(''); setPassword(''); setError('');
                  }}>Back to sign in</a>
                </div>
              </form>
            ) : forgotStep === 'email' && !mustResetPassword ? (
              <form onSubmit={handleForgotRequest}>
                <div className="aone-login-field">
                  <div className="aone-login-input-wrap">
                    <input
                      id="forgot-email"
                      className="aone-login-input"
                      type="email"
                      placeholder=" "
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      autoComplete="email"
                      data-no-uppercase="true"
                      required
                      autoFocus
                    />
                    <label htmlFor="forgot-email">Email</label>
                    <span className="aone-login-underline" />
                    <span className="aone-login-input-icon">@</span>
                  </div>
                </div>
                <button type="submit" className="aone-login-primary" disabled={loading}>
                  {loading ? 'Sending code…' : 'Email me a code'}
                </button>
                <button type="button" className="aone-login-secondary" onClick={() => { setForgotStep(null); setError(''); }}>
                  Back to sign in
                </button>
              </form>
            ) : forgotStep === 'otp' && !mustResetPassword ? (
              <form onSubmit={handleVerifyOtp}>
                <div className="aone-login-field">
                  <div className="aone-login-input-wrap">
                    <input
                      id="otp"
                      className="aone-login-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder=" "
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                      style={{ letterSpacing: 8, textAlign: 'center', fontSize: DS.type.xl, paddingRight: 0 }}
                      autoComplete="one-time-code"
                      data-no-uppercase="true"
                      required
                      autoFocus
                    />
                    <label htmlFor="otp">6-digit code</label>
                    <span className="aone-login-underline" />
                  </div>
                </div>
                <button type="submit" className="aone-login-primary" disabled={loading}>
                  {loading ? 'Verifying…' : 'Verify code'}
                </button>
                <div className="aone-login-row" style={{ marginTop: 10 }}>
                  <a className="aone-login-link" href="#resend" onClick={(e) => { e.preventDefault(); handleForgotRequest(); }}>Resend code</a>
                  <a className="aone-login-link" href="#back" onClick={(e) => { e.preventDefault(); setForgotStep(null); setError(''); }}>Back to sign in</a>
                </div>
              </form>
            ) : mustResetPassword ? (
              <form onSubmit={handleForceResetPassword}>
                <div className="aone-login-policy">
                  Password policy: min 8 chars, uppercase, lowercase, number, special character. Cannot reuse last 3 passwords.
                </div>
                <div className="aone-login-field">
                  <div className="aone-login-input-wrap">
                    <input
                      id="new-password"
                      className="aone-login-input"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder=" "
                      autoComplete="new-password"
                      data-no-uppercase="true"
                      autoFocus
                    />
                    <label htmlFor="new-password">New password</label>
                    <span className="aone-login-underline" />
                    <button type="button" className="aone-login-eye" onClick={() => setShowNewPassword(!showNewPassword)} aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
                      <EyeIcon off={showNewPassword} />
                    </button>
                  </div>
                </div>
                <div className="aone-login-field">
                  <div className="aone-login-input-wrap">
                    <input
                      id="confirm-password"
                      className="aone-login-input"
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder=" "
                      autoComplete="new-password"
                      data-no-uppercase="true"
                    />
                    <label htmlFor="confirm-password">Confirm password</label>
                    <span className="aone-login-underline" />
                  </div>
                </div>
                <button type="submit" className="aone-login-primary" disabled={loading}>
                  {loading ? (
                    <span className="aone-login-loading">
                      <svg width="17" height="17" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="rgba(244,232,215,0.35)" strokeWidth="2.5" fill="none" /><path d="M12 2a10 10 0 019.8 8" stroke="#F4E8D7" strokeWidth="2.5" fill="none" strokeLinecap="round" /></svg>
                      Updating...
                    </span>
                  ) : 'Update password and enter'}
                </button>
              </form>
            ) : (
              <>
                <form onSubmit={handleEmailLogin}>
                  <div className="aone-login-field">
                    <div className="aone-login-input-wrap">
                      <input
                        id="email"
                        className="aone-login-input"
                        type="email"
                        placeholder=" "
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        autoComplete="username webauthn"
                        data-no-uppercase="true"
                        required
                        autoFocus
                      />
                      <label htmlFor="email">{L('Email', 'Эл. почта')}</label>
                      <span className="aone-login-underline" />
                      <span className="aone-login-input-icon">@</span>
                    </div>
                  </div>
                  <div className="aone-login-field">
                    <div className="aone-login-input-wrap">
                      <input
                        id="password"
                        className="aone-login-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder=" "
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        autoComplete="current-password"
                        data-no-uppercase="true"
                        required
                      />
                      <label htmlFor="password">{L('Password', 'Пароль')}</label>
                      <span className="aone-login-underline" />
                      <button type="button" className="aone-login-eye" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        <EyeIcon off={showPassword} />
                      </button>
                    </div>
                  </div>

                  <div className="aone-login-row">
                    <label className="aone-login-check">
                      <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} />
                      {L('Remember me', 'Запомнить меня')}
                    </label>
                    <a className="aone-login-link" href="#forgot" onClick={(e) => { e.preventDefault(); setForgotStep('email'); setError(''); }}>{L('Forgot password?', 'Забыли пароль?')}</a>
                  </div>

                  <button type="submit" className="aone-login-primary" disabled={loading}>
                    {loading ? (
                      <span className="aone-login-loading">
                        <svg width="17" height="17" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="rgba(244,232,215,0.35)" strokeWidth="2.5" fill="none" /><path d="M12 2a10 10 0 019.8 8" stroke="#F4E8D7" strokeWidth="2.5" fill="none" strokeLinecap="round" /></svg>
                        {L('Signing in...', 'Вход...')}
                      </span>
                    ) : (
                      <>
                        <span className="aone-login-sheen" aria-hidden="true" />
                        <span>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 10, verticalAlign: '-2px' }}><circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.8 12.2 21 2" /><path d="M16 7l3 3" /><path d="M18.5 4.5l3 3" /></svg>
                          {partners ? L('Sign in', 'Войти') : 'Sign in'}
                        </span>
                      </>
                    )}
                  </button>
                </form>

                {passkeyReady && !partners && (
                  <>
                    <div className="aone-login-or"><span /><i>or</i><span /></div>
                    <button
                      type="button"
                      className="aone-login-passkey"
                      onClick={handlePasskeyLogin}
                      disabled={passkeyBusy || loading}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 11c0-3 1-5 4-5s4 2 4 5-1 5-4 5" />
                        <path d="M4 11c0-3 1-5 4-5" />
                        <path d="M8 11v4c0 2-1 4-1 4" />
                        <path d="M12 15v3" />
                        <circle cx="8" cy="11" r="0.5" fill="currentColor" />
                      </svg>
                      {passkeyBusy ? 'Waiting for your device…' : 'Sign in with Touch ID'}
                    </button>
                    {passkeyHint && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted, #6B615A)', textAlign: 'center' }}>{passkeyHint}</div>}
                  </>
                )}

                {partners && (
                  <p style={{ textAlign: 'center', fontSize: DS.type.sm, margin: '14px 0 0', color: 'var(--muted, #6B615A)' }}>
                    {L('Not a partner yet?', 'Ещё не партнёр?')} <a className="aone-login-link" href="/partners/apply" onClick={(e) => { e.preventDefault(); navigate('/partners/apply'); }}>{L('Apply for access', 'Запросить доступ')}</a>
                  </p>
                )}
                {partners ? (
                  <p className="pp-login-foot">{L('Secure sign-in · Your data stays private to your company', 'Защищённый вход · Данные видны только вашей компании')}</p>
                ) : (
                  <div className="aone-login-security">
                    <div>Private</div>
                    <div>Audited</div>
                    <div>Encrypted</div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
